import {collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where,} from '@firebase/firestore'
import {FirebaseAuthentication} from '@capacitor-firebase/authentication'

import { defineStore } from 'pinia'
import { Locales, User } from '~~/types/types'
import { UserStates } from '~~/types/enums'
import { deleteUser, getAuth, onAuthStateChanged } from '@firebase/auth'
import { Capacitor } from '@capacitor/core'
import { Buffer } from 'buffer';
import { isEqual } from 'lodash'
import useAuth from '~/composables/useAuth'
import { stampTenantId, tenantScopeConstraints } from '~/composables/useTenantScope'
import { ensureTenantLoaded } from '~/composables/useTenant'

// App-wide fallback locale for user docs that carry no explicit `locale` (e.g.
// a freshly bootstrapped social-login user). 'de' so it agrees with the tenant
// default and never overrides it with English (see plugins/i18n.ts).
const baseLocale = 'de'

export const useUserStore = defineStore({
  id: 'user',
  state: () => {
    return {
      user: {
        uid: '',
        email: '',
        firstName: '',
        lastName: '',
        displayName: '',
        photoURL: null,
        locale: baseLocale,
        hasPassword: false,
        emailVerified: false,
        isAdmin: false,
      } as User,
      userRef: null as any,
      firebaseUser: null as null | FirebaseUser,
      snapShotListener: [] as Unsubscribe[],
      localeChangeListener: null as Function | null,
      justLoggedIn: false, // used in "ensureStateInSync" to let system know that we just logged in and it's okay if state is not in sync
    }
  },
  actions: {
    async setup(callback?: Function | null) {
      console.debug('UserStore:setup')
      useAuth()
      if (this.isIOSDevice) {
        getAuth().onAuthStateChanged(async (u) => {
          console.debug('UserStore:onAuthStateChanged getAuth', u)
          await this.refreshState(u)
        })
      } else {
        await FirebaseAuthentication.removeAllListeners()

        FirebaseAuthentication.addListener('authStateChange', async (u) => {
          console.debug('UserStore:onAuthStateChanged FirebaseAuthentication', u)
          await this.refreshState(u)
        })
      }

    },
    async refreshState(u, params = {}) {
      if (!u) {
        console.debug('UserStore: authStateChange did not provide a user')
        return
      }
      const user = this.isIOSDevice ? u : u?.user
      if (user) {
        this.$patch((state) => (state['firebaseUser'] = user))
        const store = useNuxtApp().$db
        const ref = doc(store, 'users', user.uid)
        this.$patch((state) => (state['userRef'] = ref))
        let initialState = {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          displayName: user?.displayName || '',
          photoURL: user?.photoUrl || '',
          locale: user?.locale || baseLocale,
        }
        if (params) {
          initialState = { ...initialState, ...params };
        }
        this.$patch((state) => (state['user'] = {
          ...state['user'],
          ...initialState,
        }));

        // The race condition note remains the same.
        // Using the useful parts of this user here as well.
        const loadUser = async () => {
          this.justLoggedIn = false
          // In multi-tenant mode the auth token must be scoped to the tenant
          // (getAuth().tenantId) before any Firestore read, or the rules reject
          // it with "permission denied". This call resolves the tenant (no-op in
          // single mode) and is memoized, so awaiting it here just synchronises
          // the first read with tenant resolution instead of racing it.
          await ensureTenantLoaded()
          const docSnap = await getDoc(ref)
          if (docSnap.exists()) {
            const data = docSnap.data()
            const loadedUser = {
              uid: initialState.uid,
              email: initialState.email,
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              displayName: data.displayName ? data.displayName : (initialState?.displayName ? initialState?.displayName : null),
              photoURL: data.photoURL ? data.photoURL : (initialState?.photoURL ? initialState?.photoURL : null),
              locale: data.locale || baseLocale,
              hasPassword: data.hasPassword || false,
              emailVerified: initialState.emailVerified,
              isAdmin: data.isAdmin || false,
              // Preserve the existing tenantId so the equality check below is
              // honest (data carries tenantId too) and the merge-write never
              // strips it. stampTenantId would otherwise re-derive it and, if the
              // tenant were not yet resolved, overwrite it with '' — which drops
              // the user from tenant-scoped list queries.
              tenantId: data.tenantId,
            };
            // Only write back when something actually changed. stampTenantId is a
            // no-op in single mode; in multi mode loadedUser already carries the
            // preserved tenantId, so the write can never empty it.
            if (!isEqual(data, stampTenantId(loadedUser)) && ref) {
              await setDoc(ref, stampTenantId(loadedUser), {merge: true})
            }
            this.$patch((state) => (state['user'] = loadedUser))
            if (this.localeChangeListener) {
              this.localeChangeListener(loadedUser.locale)
            }
            return loadedUser
          } else {
            // Bootstrap the user doc client-side. Required for Identity Platform
            // tenants because functions.auth.user().onCreate does not fire for
            // tenant-scoped users. Safe in single mode too — the Cloud Function
            // trigger also writes with merge:true, so the later write wins
            // without data loss.
            const bootstrap = {
              uid: initialState.uid,
              email: '',
              displayName: initialState.displayName || '',
              photoURL: initialState.photoURL || '',
              locale: baseLocale,
            }
            await setDoc(ref, stampTenantId(bootstrap), { merge: true })
            console.debug(`UserStore: bootstrapped user doc for ${initialState.uid}`)
            return false
          }
        };

        // A throw here (e.g. a transient "permission denied" before the tenant
        // token is ready) must fall through to the retry loop, not propagate as
        // an unhandled rejection that leaves the store with stale defaults
        // (isAdmin=false) and never retries.
        let loadedUser: Awaited<ReturnType<typeof loadUser>> | null = null
        try {
          loadedUser = await loadUser()
        } catch (e) {
          console.warn('UserStore: initial loadUser failed, will retry', e)
        }
        if (loadedUser && loadedUser.uid) {
          console.debug('UserStore: user loaded immediately')
        } else {
          console.debug('UserStore: user not loaded immediately, trying again in 5 seconds')
          let retryCounter = 0
          const interval = window.setInterval(async () => {
            // Stop polling if the user has logged out in the meantime. The
            // registration flow logs out immediately after bootstrapping the
            // user doc, which would otherwise leave this interval running
            // unauthenticated and spamming "permission denied" forever.
            if (!this.firebaseUser) {
              window.clearInterval(interval)
              return
            }
            // A failed read/write (e.g. transient permission error) must not
            // skip the counter increment / clear logic below, or the interval
            // would retry forever. Swallow and let the retry budget bound it.
            try {
              await loadUser()
            } catch (e) {
              console.warn('UserStore: loadUser retry failed', e)
            }
            if (this.user.uid) {
              window.clearInterval(interval)
              console.debug(`UserStore: user ${this.user.uid} loaded with delay`)
            }
            if (retryCounter > 10) {
              window.clearInterval(interval)
              console.error('UserStore: user not loaded after retries, giving up')
            }
            retryCounter++
          }, 1000)
        }
      } else {
        console.debug('UserStore: authStateChange did not provide a valid user, resetting state')
        // Consider whether you want to log out here or just navigate.
        const router = useNuxtApp().$router
        if (router.currentRoute.value.meta.accesscontrol !== 'public') {
          router.push('/register')
        }
      }
    },
    ensureStateInSync() {
      console.debug('UserStore:ensureStateInSync')
      if (!this.firebaseUser && !this.justLoggedIn) {
        if (this.isIOSDevice) {
          onAuthStateChanged(getAuth(), async (u) => {
            try {
              await this.refreshState(u)
            } catch (e) {
              console.error('Could not refresh state - getAuth')
              await this.logout()
            }
          })
        } else {
          FirebaseAuthentication.getCurrentUser().then(async (u) => {
            try {
              await this.refreshState(u)
            } catch (e) {
              console.error('Could not refresh state - FirebaseAuthentication')
              await this.logout()
            }
          })
        }
      }
    },
    async logout() {
      this.snapShotListener.forEach((unssubscribe) => {
        unssubscribe()
      })
      await FirebaseAuthentication.signOut()
      if (Capacitor.isNativePlatform()) {
        // Sign in also on the web layer
        const auth = getAuth()
        await auth.signOut()
        console.debug('Logout on Web successful')
      }
      this.$reset()
    },
    addUnsusbscribeSnapshotListener(listener: Function | null) {
      this.snapShotListener.push(listener)
    },
    async homepage(): Promise<string> {
      const userRef = this.userRef
      if (!userRef) {
        return '/'
      }
      const user = await getDoc(userRef)
      if (user.data()) {
        const userData = user.data() as User
        if (!userData.hasPassword) {
          // this might be a code repetition
          const store = useNuxtApp().$db
          const collectionRef = collection(store, 'camera-rolls')
          const q = query(collectionRef, ...tenantScopeConstraints(), where('members', 'array-contains', userRef))
          const docsSnapshot = await getDocs(q)
          if (docsSnapshot.docs.length === 1) {
            console.debug('Redirecting to the one camera-roll where the user has access to')
            return `/camera-roll/${docsSnapshot.docs[0].id}`
          }
        }
      }
      return `/camera-rolls`
    },
    async updateName(name: string) {
      const userRef = this.userRef
      const user = await getDoc(userRef)
      const userData = user.data() as User
      if (userData) {
        userData.displayName = name
        this.$patch((state) => (state.user.displayName = userData.displayName))
        await setDoc(userRef, stampTenantId(userData), { merge: true })
      }
      return userRef
    },
    async updateNameEmail(name: string, email?: string, firstName?: string, lastName?: string) {
      const userRef = this.userRef
      const user = await getDoc(userRef)
      if (user.data()) {
        const userData = user.data() as User
        if (userData) {
          // update in Firestore
          userData.displayName = name
          if (firstName !== undefined) userData.firstName = firstName
          if (lastName !== undefined) userData.lastName = lastName
          this.$patch((state) => {
            state.user.displayName = userData.displayName
            if (firstName !== undefined) state.user.firstName = firstName
            if (lastName !== undefined) state.user.lastName = lastName
          })
          if (email) {
            this.$patch((state) => (state.user.email = email.toLowerCase()))
            await FirebaseAuthentication.updateEmail({
              newEmail: email,
            })
          }
          await setDoc(userRef, stampTenantId(userData), { merge: true })

          if (email) {
            const store = useNuxtApp().$db
            // @tsignore
            const publicRef = doc(store, 'users-public', this.user.uid)
            const msgBuffer = new TextEncoder().encode(email.toLowerCase())
            const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
            await setDoc(publicRef, stampTenantId({ email: Buffer.from(hashBuffer).toString("base64") }), { merge: true })
            console.debug('local store updated')
          }
        }
      }
      return userRef
    },
    async updateAvatar(url: string) {
      await updateDoc(this.userRef, { photoURL: url })
      this.$patch((state) => (state.user.photoURL = url))
    },
    // Marks the user as guest.
    async setUserToGuest(isGuest: boolean) {
      let userRef = this.userRef
      if (!userRef) {
        this.setup(async () => {
          const store = useNuxtApp().$db
          if (this.firebaseUser) {
            // @todo Code duplication, needs to be fixed
            userRef = doc(store, 'users', this.firebaseUser.uid)
            await setDoc(userRef, stampTenantId({ hasPassword: !isGuest }), { merge: true })
            console.debug(`hasPassword set to ${!isGuest} in user ${userRef.id}.`)
            this.$patch((state) => (state.user.hasPassword = !isGuest))
          } else {
            throw new Error('No firebase user retrieable via userStore')
          }
        })
      }
      await setDoc(userRef, stampTenantId({ hasPassword: !isGuest }), { merge: true })
      console.debug(`hasPassword set to ${!isGuest} in user ${userRef.id}.`)
      this.$patch((state) => (state.user.hasPassword = !isGuest))
    },
    async deleteCurrentUser() {
      const user = this.firebaseUser
      if (user) {
        await deleteDoc(this.userRef)

        const auth = getAuth()
        deleteUser(auth.currentUser!)
          .then(() => {
            console.debug('FirebaseAuth User deleted')
          })
          .catch((error) => {
            console.error('FirebaseAuth User deletion failed', error)
          })

        // @todo Remove user fromall camera-rolls where he is a member.
        // Delete his photos

        await this.logout()
        console.debug('User deleted')
      } else {
        console.debug('User not deleted')
      }
    },
    // @todo Remove this if not used
    async isLoggedIn() {
      return true
    },
    async getCurrentUser() {
      /* For iOS devices, the current user must be taken from the Firebase SDK */
      if (this.isIOSDevice) {
        return getAuth().currentUser
      }
      const currentUser = await FirebaseAuthentication.getCurrentUser()
      return currentUser.user
    },
    async setLocale(locale: string) {
      console.debug(`UserStore: setLocale ${locale}`)

      let userRef = this.userRef
      this.$patch((state) => (state.user.locale = locale as Locales))
      await setDoc(userRef, stampTenantId({ locale: locale as Locales }), { merge: true })
    },
    subscribeToLocaleChange(f: any) {
      console.debug('UserStore: subscribeToLocaleChange')
      this.$patch((state) => (state.localeChangeListener = f))
    }
  },
  getters: {
    userState(state): UserStates | null {
      console.debug('calling getter')
      const { user, firebaseUser } = state

      if (!user || !user.uid || !firebaseUser) return null

      const isAnonymous = firebaseUser.isAnonymous || (firebaseUser.user && firebaseUser.user.isAnonymous)
      if (isAnonymous) {
        return user.displayName ? UserStates.NamedGuest : UserStates.AnonymousGuest
      }
      return UserStates.Registered
    },
    isGuest(state): boolean {
      return state.userState === UserStates.NamedGuest || state.userState === UserStates.RecognizableGuest
    },
    isIOSDevice(): boolean {
      return Capacitor.getPlatform() == 'ios'
    },
    isAdmin(state): boolean {
      return state.user.isAdmin === true
    }
  },
})
